import { Icon } from "@iconify/react/dist/iconify.js";
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "./ui/LoadingComponent";
import TableDataNoAvailable from "./ui/TableDataNoAvailable";
import PaginationControls from "./ui/PaginationControls";
import PerPageDropdown from "./ui/PerPageDropdown";
import { usePermissions } from "../context/PermissionContext";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import SortIcons from "./ui/SortIcons";
import ErrorAlert from "./ui/ErrorAlert";

const UsersList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const [modalType, setModalType] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalError, setModalError] = useState("");
  const [modalSuccess, setModalSuccess] = useState("");
  const [roleId, setRoleId] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const handleSubmit = () => {
    handleDeleteRole();
  };

  const handleDeleteRole = async () => {
    setDeletedLoading(true);
    if (!roleId || isNaN(roleId)) {
      handleCustomResponse("error", "Invalid or missing Role ID.");
      setDeletedLoading(false);
      return;
    }
    try {
      const response = await apiCall("delete", `/delete-user/${roleId}`);
      if (response?.data?.success) {
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "DELETE");
        setModalError("");
        handleCloseModal();
        fetchUsers(currentPage, perPage, searchQuery);
      } else {
        handleCustomResponse("error", "Failed to delete user.");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "DELETE");
      setModalSuccess("");
    } finally {
      setDeletedLoading(false);
    }
  };

  const handleOpenModal = async (type, user = null) => {
    setModalType(type);
    setShowModal(true);
    setModalError("");
    setModalSuccess("");
    setRoleId(null);
    setSelectedRole(user);
    if (type === "delete" && user) {
      if (!user.user_id || isNaN(user.user_id)) {
        handleCustomResponse(
          "error",
          "Invalid or missing User ID for this user."
        );
        return;
      }
      setRoleId(user.user_id);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalType("");
    setRoleId(null);
    setModalError("");
    setModalSuccess("");
    setSelectedRole(null);
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
    fetchUsers(
      currentPage,
      perPage,
      searchQuery,
      field,
      sortConfig.field === field && sortConfig.direction === "asc"
        ? "desc"
        : "asc"
    );
  };

  const fetchUsers = async (
    page = 1,
    perPage = 10,
    search = "",
    sortBy = sortConfig.field,
    sortOrder = sortConfig.direction
  ) => {
    try {
      setLoading(true);
      setErrorMessage("");

      const queryParams = new URLSearchParams();
      queryParams.append("search", search);
      queryParams.append("page", page);
      queryParams.append("per_page", perPage);
      if (sortBy) {
        queryParams.append("sort_by", sortBy);
        queryParams.append("sort_order", sortOrder);
      }

      const response = await apiCall(
        "get",
        `/get-all-users?${queryParams.toString()}`
      );

      if (response?.data?.success) {
        let userList = [];
        let total = 0;
        let currentPageFromResponse = page;
        let lastPage = 1;
        let perPageFromResponse = perPage;

        if (Array.isArray(response.data.data)) {
          userList = response.data.data.map((user) => ({
            ...user,
            display_last_login: user.last_login
              ? new Date(user.last_login).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })
              : "-",
            display_created_datetime: user.created_datetime
              ? new Date(user.created_datetime).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })
              : "-",
          }));
          total = response.data.pagination?.total || userList.length;
          currentPageFromResponse =
            response.data.pagination?.current_page || page;
          lastPage = response.data.pagination?.last_page || 1;
          perPageFromResponse = response.data.pagination?.per_page || perPage;
        }

        setUsers(userList);
        setTotalEntries(total);
        setCurrentPage(parseInt(currentPageFromResponse, 10));
        setTotalPages(parseInt(lastPage, 10));
        setPerPage(parseInt(perPageFromResponse, 10));

        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "GET");
      } else {
        handleCustomResponse("error", "Failed to load users.");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "GET");
      setErrorMessage(true);
    } finally {
      setLoading(false);
    }
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const debouncedFetchUsers = useCallback(
    debounce((page, perPage, search) => {
      fetchUsers(page, perPage, search, sortConfig.field, sortConfig.direction);
    }, 500),
    [sortConfig]
  );

  useEffect(() => {
    fetchUsers(currentPage, perPage, searchQuery);
  }, [currentPage, perPage]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCurrentPage(1);
    debouncedFetchUsers(1, perPage, value);
  };

  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const startEntry = totalEntries > 0 ? (currentPage - 1) * perPage + 1 : 0;
  const endEntry = Math.min(currentPage * perPage, totalEntries);

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (endPage - startPage < maxPagesToShow - 1) {
      if (startPage === 1) {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      } else if (endPage === totalPages) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };
  if (errorMessage) {
    return <ErrorAlert />;
  }

  return (
    <div className="p-4 p-md-3 p-sm-2">
      <div className="card shadow-sm">
        <div className="card p-3 p-sm-2 p-md-3">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <div
              className="icon-field flex-grow-1"
              style={{ maxWidth: "400px" }}
            >
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search by username..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <span className="icon">
                <Icon icon="ion:search-outline" />
              </span>
            </div>
            <PerPageDropdown
              perPage={perPage}
              handlePerPageChange={(value) => {
                setPerPage(value);
                setCurrentPage(1);
              }}
            />
            <div className="ms-auto">
              {hasPermission("insert_user") && (
                <Link
                  to="/add-user"
                  className="btn btn-primary-600 btn-sm d-flex align-items-center"
                >
                  <Icon icon="ri:add-line" className="me-1" /> Add User
                </Link>
              )}
            </div>
          </div>

          {errorMessage && (
            <div
              className="alert alert-danger alert-dismissible fade show mb-3"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
              ></button>
            </div>
          )}

          {users.length === 0 && !loading ? (
            <TableDataNoAvailable message="No User found." />
          ) : (
            <div className="position-relative">
              <div
                className="table-responsive"
                style={{
                  opacity: loading ? 0.4 : 1,
                  pointerEvents: loading ? "none" : "auto",
                }}
              >
                <table className="table bordered-table mb-0 table-hover">
                  <thead>
                    <tr style={{ backgroundColor: "#475EAB", color: "white" }}>
                      <th scope="col" className="p-2">
                        <span>S.No</span>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("username")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Username</span>
                          <SortIcons field="username" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("email")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Email</span>
                          <SortIcons field="email" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("role")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Role</span>
                          <SortIcons field="role" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("created_datetime")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Created Date</span>
                          <SortIcons
                            field="created_datetime"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("last_login")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Last Login</span>
                          <SortIcons
                            field="last_login"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("is_active")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex">
                          <span>Status</span>
                          <SortIcons
                            field="is_active"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th scope="col" className="p-2">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, index) => (
                      <tr key={user.user_id || index}>
                        <td className="p-2">
                          {(currentPage - 1) * perPage + index + 1}
                        </td>
                        <td className="p-2">
                          <span className="">
                            {user.username || `User #${user.user_id}`}
                          </span>
                        </td>
                        <td className="p-2">{user.email || "-"}</td>
                        <td className="p-2">
                          <Link to={`/all-roles`} className="text-primary-600">
                            {user.role || "-"}
                          </Link>
                        </td>
                        <td className="p-2">{user.display_created_datetime}</td>
                        <td className="p-2">{user.display_last_login}</td>
                        <td className="p-2">
                          <span
                            className={`px-3 py-1 radius-4 fw-medium text-sm ${
                              user.is_active
                                ? "bg-success-focus text-success-600 border border-success-main"
                                : "bg-danger-focus text-danger-600 border border-danger-main"
                            }`}
                          >
                            {user.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="p-2">
                          {hasPermission("update_user") && (
                            <Link to={`/edit-user/${user?.user_id}`}>
                              <button className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center">
                                <Icon icon="lucide:edit" />
                              </button>
                            </Link>
                          )}
                          {hasPermission("delete_user") && (
                            <button
                              className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                              style={{
                                position: "relative",
                                transition: "all 0.3s ease",
                                cursor: "pointer",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.1)";
                                const tooltip = document.createElement("div");
                                tooltip.textContent = "Delete";
                                tooltip.style.cssText = `
                                  position: absolute;
                                  top: -35px;
                                  left: 50%;
                                  transform: translateX(-50%);
                                  background-color: #333;
                                  color: white;
                                  padding: 5px 8px;
                                  border-radius: 4px;
                                  font-size: 12px;
                                  white-space: nowrap;
                                  z-index: 1000;
                                  pointer-events: none;
                                `;
                                tooltip.id = "edit-tooltip";
                                e.currentTarget.appendChild(tooltip);
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)";
                                const tooltip =
                                  document.getElementById("edit-tooltip");
                                if (tooltip) tooltip.remove();
                              }}
                              onClick={() => handleOpenModal("delete", user)}
                            >
                              <Icon icon="mingcute:delete-2-line" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {loading && (
                <div
                  className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
                  style={{
                    zIndex: 10,
                    borderRadius: "10px",
                    padding: "20px",
                  }}
                >
                  <LoadingComponent />
                </div>
              )}
              <PaginationControls
                pagination={{
                  currentPage,
                  perPage,
                  total: totalEntries,
                }}
                handlePageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
      <div
        className={`modal fade ${showModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showModal ? "rgba(0,0,0,0.5)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Delete User</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseModal}
              ></button>
            </div>
            <div className="modal-body">
              {modalError && (
                <div className="alert alert-danger">{modalError}</div>
              )}
              {modalSuccess && (
                <div className="alert alert-success">{modalSuccess}</div>
              )}
              <p>
                Are you sure you want to delete{" "}
                <strong>{selectedRole?.username || "this User"}</strong>?
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                disabled={deletedLoading}
                type="button"
                className="btn btn-danger"
                onClick={handleSubmit}
              >
                {deletedLoading ? "Please wait..." : "Delete User"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsersList;
